import { useEffect, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { fetchCatalogItem } from "../api.js";
import Breadcrumbs from "../components/Breadcrumbs.jsx";
import CatalogItemDetail from "../components/CatalogItemDetail.jsx";

export default function CatalogItemPage() {
  const { sku } = useParams();
  const { page } = useOutletContext() ?? {};
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!sku) {
      setLoading(false);
      setNotFound(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);
        const data = await fetchCatalogItem(sku);
        if (cancelled) return;
        setItem(data);
      } catch (e) {
        if (cancelled) return;
        if (e?.notFound) setNotFound(true);
        else setError(e?.message || String(e));
        setItem(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sku]);

  useEffect(() => {
    if (!item) return;
    const base = page?.title ? page.title.split("—")[0].trim() : "PMP";
    document.title = `${item.name} · ${base}`;
    return () => {
      if (page?.title) document.title = page.title;
    };
  }, [item, page?.title]);

  if (loading) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-5 d-flex justify-content-center">
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Loading</span>
          </Spinner>
        </Container>
      </section>
    );
  }

  if (notFound || !sku) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-4">
          <p className="pmp-kicker mb-2">Catalog</p>
          <h1 className="h3 mb-3">Item not found</h1>
          <p className="text-secondary mb-4">No product matches this SKU.</p>
          <Link to="/catalog" className="btn btn-primary">
            Back to catalog
          </Link>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5 pmp-section-alt">
        <Container className="py-4">
          <Alert variant="danger" className="mb-4 shadow-sm">
            {error}
          </Alert>
          <Link to="/catalog" className="btn btn-outline-primary">
            Back to catalog
          </Link>
        </Container>
      </section>
    );
  }

  return (
    <section className="py-5 pmp-section-alt flex-grow-1">
      <Container>
        <Breadcrumbs
          className="mb-4"
          items={[
            { label: "Catalog", to: "/catalog" },
            item.category && {
              label: item.category,
              to: `/catalog?category=${encodeURIComponent(item.category)}`,
            },
            item.subCategory && {
              label: item.subCategory,
              to: `/catalog?category=${encodeURIComponent(item.category)}&sub=${encodeURIComponent(item.subCategory)}`,
            },
            { label: item.name },
          ]}
        />
        <CatalogItemDetail item={item} />
      </Container>
    </section>
  );
}
